import React, { useMemo, useEffect, useRef, useState } from 'react';
import { HiX } from 'react-icons/hi';
import { SoldTicket, Ticket } from '../../../types/ticket.types';
import ChannelInitialIcon from '../../ui/ChannelInitialIcon/ChannelInitialIcon';
import UserProfileLink from '../../ui/UserDisplay/UserProfileLink';
import { useAnimatedVisibility } from '../../../hooks/useAnimatedVisibility';
import styles from './ParticipantListModal.module.css';

const LCV_TICKET_TYPE = 'togather-join-ticket';

const isLcvJoinTicketType = (type?: string) =>
  (type || '').trim().toLowerCase() === LCV_TICKET_TYPE;

// Color palette for avatar backgrounds (alphabetically consistent)
const AVATAR_COLORS = [
  '#EF4444', // Red
  '#F97316', // Orange
  '#F59E0B', // Amber
  '#EAB308', // Yellow
  '#84CC16', // Lime
  '#22C55E', // Green
  '#10B981', // Emerald
  '#14B8A6', // Teal
  '#06B6D4', // Cyan
  '#0EA5E9', // Sky
  '#3B82F6', // Blue
  '#6366F1', // Indigo
  '#8B5CF6', // Violet
  '#A855F7', // Purple
  '#D946EF', // Fuchsia
  '#EC4899', // Pink
  '#F43F5E', // Rose
  '#64748B', // Slate
];

// Get color for a name alphabetically
const getColorForName = (name: string): string => {
  if (!name || name.length === 0) {
    return AVATAR_COLORS[0];
  }
  const firstLetter = name.charAt(0).toUpperCase();
  const charCode = firstLetter.charCodeAt(0);
  // Map A-Z (65-90) to color index
  const index = (charCode - 65) % AVATAR_COLORS.length;
  return AVATAR_COLORS[index];
};

interface ParticipantListModalProps {
  isOpen: boolean;
  onClose: () => void;
  participants: SoldTicket[];
  tickets?: Ticket[];
  getTranslation: (key: string) => string;
}

const ParticipantListModal: React.FC<ParticipantListModalProps> = ({
  isOpen,
  onClose,
  participants,
  tickets = [],
  getTranslation
}) => {
  const modalContentRef = useRef<HTMLDivElement>(null);
  const [selectedTicketType, setSelectedTicketType] = useState<string | null>(null);
  const { shouldRender, isClosing, onAnimationEnd } = useAnimatedVisibility({
    isOpen,
    closeDuration: 300,
  });

  // Normalize tickets to always be an array (handle null, undefined, or non-array values)
  const normalizedTickets = useMemo(() => {
    if (!Array.isArray(tickets)) return [];
    return tickets.filter((ticket) => !isLcvJoinTicketType(ticket.type));
  }, [tickets]);

  // Create ticket map for quick lookup
  const ticketMap = useMemo(() => {
    const map = new Map<string, Ticket>();
    normalizedTickets.forEach(ticket => {
      map.set(ticket.ticketID, ticket);
    });
    return map;
  }, [normalizedTickets]);

  // Get unique ticket types excluding system join ticket types.
  const ticketTypes = useMemo(() => {
    const types = new Set<string>();
    normalizedTickets.forEach(ticket => {
      const ticketType = (ticket.type || '').trim().toLowerCase();
      if (ticket.type && ticketType !== 'join' && !isLcvJoinTicketType(ticketType)) {
        types.add(ticket.type);
      }
    });
    return Array.from(types).sort();
  }, [normalizedTickets]);

  // Show filter only if there are non-join ticket types
  const showFilter = ticketTypes.length > 0;

  // Get ticket info for a participant
  const getTicketInfo = (participant: SoldTicket): Ticket | null => {
    return ticketMap.get(participant.ticketID) || null;
  };

  // Parse created-at timestamp: API returns createdDateTime (Unix seconds string), type has createdAt (number)
  const getCreatedAtTimestamp = (p: SoldTicket): number => {
    const v = p.createdAt;
    if (typeof v === 'number' && !Number.isNaN(v)) return v;
    if (typeof v === 'string') {
      const t = new Date(v).getTime();
      if (!Number.isNaN(t)) return t;
    }
    // API field: createdDateTime (e.g. "1767967290" = Unix seconds)
    const raw = (p as unknown as { createdDateTime?: string | number }).createdDateTime;
    if (raw != null) {
      const sec = typeof raw === 'number' ? raw : parseInt(String(raw), 10);
      if (!Number.isNaN(sec)) return sec * 1000; // seconds -> ms
    }
    return 0;
  };

  // Sort by createdAt descending (newest first) and filter by selected ticket type
  const filteredParticipants = useMemo(() => {
    const sorted = [...participants].sort((a, b) => {
      const tsA = getCreatedAtTimestamp(a);
      const tsB = getCreatedAtTimestamp(b);
      return tsB - tsA;
    });
    if (!selectedTicketType) return sorted;
    return sorted.filter(participant => {
      const ticketInfo = ticketMap.get(participant.ticketID);
      return ticketInfo && ticketInfo.type === selectedTicketType;
    });
  }, [participants, selectedTicketType, ticketMap]);

  // Get profile photo URL (check user.userAvatar first, then fallback to other fields)
  const getProfilePhoto = (participant: SoldTicket): string | null => {
    return participant.user?.userAvatar || participant.userAvatar || participant.user?.avatar || participant.avatar || participant.user?.image || participant.image || null;
  };

  // Get display name
  const getDisplayName = (participant: SoldTicket): string => {
    return `${participant.name} ${participant.lastname}`.trim();
  };

  // Reset filter when modal opens/closes
  useEffect(() => {
    if (isOpen) {
      setSelectedTicketType(null);
    }
  }, [isOpen]);

  // Handle escape key to close modal
  useEffect(() => {
    if (!isOpen) return;

    const handleEscape = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        onClose();
      }
    };

    document.addEventListener('keydown', handleEscape);
    return () => {
      document.removeEventListener('keydown', handleEscape);
    };
  }, [isOpen, onClose]);

  // Focus trap for accessibility
  useEffect(() => {
    if (!isOpen || !modalContentRef.current) return;

    const modal = modalContentRef.current;
    const focusableElements = modal.querySelectorAll(
      'button, [href], input, select, textarea, [tabindex]:not([tabindex="-1"])'
    );
    const firstElement = focusableElements[0] as HTMLElement;
    const lastElement = focusableElements[focusableElements.length - 1] as HTMLElement;

    const handleTab = (e: KeyboardEvent) => {
      if (e.key !== 'Tab') return;

      if (e.shiftKey) {
        if (document.activeElement === firstElement) {
          e.preventDefault();
          lastElement?.focus();
        }
      } else {
        if (document.activeElement === lastElement) {
          e.preventDefault();
          firstElement?.focus();
        }
      }
    };

    document.addEventListener('keydown', handleTab);
    firstElement?.focus();

    return () => {
      document.removeEventListener('keydown', handleTab);
    };
  }, [isOpen]);

  if (!shouldRender) return null;

  return (
    <div
      className={`${styles.modalOverlay} ${isClosing ? styles.modalOverlayClosing : ''}`}
      onClick={onClose}
      onAnimationEnd={onAnimationEnd}
      role="dialog"
      aria-modal="true"
      aria-labelledby="participant-modal-title"
    >
      <div
        className={`${styles.modalContent} ${isClosing ? styles.modalContentClosing : ''}`}
        onClick={(e) => e.stopPropagation()}
        ref={modalContentRef}
      >
        <div className={styles.modalHeader}>
          <h3 className={styles.modalTitle} id="participant-modal-title">
            {getTranslation('events.participants')}
          </h3>
          <button
            className={styles.closeButton}
            onClick={onClose}
            aria-label={getTranslation('common.close')}
          >
            {/* eslint-disable-next-line @typescript-eslint/no-unsafe-assignment */}
            {/* @ts-ignore - react-icons compatibility with React 19 */}
            <HiX className={styles.closeIcon} />
          </button>
        </div>
        
        {showFilter && (
          <div className={styles.filterContainer}>
            <button
              className={`${styles.filterButton} ${selectedTicketType === null ? styles.filterButtonActive : ''}`}
              onClick={() => setSelectedTicketType(null)}
            >
              {getTranslation('events.allTicketTypes') || 'Tüm Bilet Türleri'}
            </button>
            {ticketTypes.map((type) => (
              <button
                key={type}
                className={`${styles.filterButton} ${selectedTicketType === type ? styles.filterButtonActive : ''}`}
                onClick={() => setSelectedTicketType(type)}
              >
                {type}
              </button>
            ))}
          </div>
        )}
        
        <div className={styles.participantsList}>
          {filteredParticipants.length === 0 ? (
            <div className={styles.emptyState}>
              <p>{getTranslation('events.noParticipants') || 'Henüz katılımcı yok'}</p>
            </div>
          ) : (
            filteredParticipants.map((participant, index) => {
              const photoUrl = getProfilePhoto(participant);
              const displayName = getDisplayName(participant);
              const ticketInfo = getTicketInfo(participant);

              return (
                <UserProfileLink
                  key={`${participant.ticketNo || participant.email}-${index}`}
                  userId={(participant as any).userID || (participant as any).id}
                  name={participant.name}
                  lastname={participant.lastname}
                >
                  <div className={styles.participantItem}>
                    <div className={styles.avatarWrapper}>
                      <div className={styles.participantAvatar}>
                        {photoUrl ? (
                          <img
                            src={photoUrl}
                            alt={displayName}
                            className={styles.avatarImage}
                            onError={(e) => {
                              const target = e.target as HTMLImageElement;
                              target.style.display = 'none';
                              const parent = target.parentElement;
                              if (parent) {
                                const fallback = parent.querySelector(`.${styles.avatarFallback}`);
                                if (fallback) {
                                  (fallback as HTMLElement).style.display = 'flex';
                                }
                              }
                            }}
                          />
                        ) : null}
                        <div
                          className={styles.avatarFallback}
                          style={{
                            display: photoUrl ? 'none' : 'flex',
                            backgroundColor: getColorForName(displayName),
                            color: '#ffffff'
                          }}
                        >
                          <ChannelInitialIcon
                            channelName={displayName}
                            size={48}
                          />
                        </div>
                      </div>
                    </div>
                    <div className={styles.participantInfo}>
                      <div className={styles.participantNameRow}>
                        <p className={styles.participantName}>{displayName}</p>
                        {ticketInfo && ticketInfo.type && (
                          <span className={styles.ticketTypeBadge}>{ticketInfo.type}</span>
                        )}
                      </div>
                    </div>
                  </div>
                </UserProfileLink>
              );
            })
          )}
        </div>
      </div>
    </div>
  );
};

export default ParticipantListModal;
